import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import DynamicTable from '../../components/Table/DynamicTable';
import Pagination from '../../components/Pagination/Pagination';
import FormSpinnerOverlay from '../../components/ui/FormSpinnerOverlay';
import AlertMessage from '../../components/ui/AlertMessage';
import SearchIcon from '../../components/icons/SearchIcon';
import EyeIcon from '../../components/icons/EyeIcon';
import TrashFillIcon from '../../components/icons/TrashFillIcon';

const PaymentListAdminScreen = () => {
    const navigate = useNavigate();
    const [payments, setPayments] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    // Mock data for payments, simulating an API response
    const mockPayments = [
        { id: 'TRN-00123', clientName: 'Juan Pérez Morales', contractId: 'CONTR-PREM-0724', scheduleDate: '2024-07-05', travelDateRange: '2024-08-01 a 2024-08-10', paymentDate: '2024-07-15', amount: '$25.000 CLP', method: 'Tarjeta de Crédito', status: 'Pagado', statusClass: 'bg-success' },
        { id: 'TRN-00124', clientName: 'Ana García López', contractId: 'CONTR-PREM-0725', scheduleDate: '2024-07-06', travelDateRange: '2024-09-05 a 2024-09-15', paymentDate: '2024-07-20', amount: '$150.000 CLP', method: 'Transferencia', status: 'Pagado', statusClass: 'bg-success' },
        { id: 'TRN-00125', clientName: 'Carlos Sánchez', contractId: 'CONTR-PREM-0726', scheduleDate: '2024-07-08', travelDateRange: '2024-08-20 a 2024-08-30', paymentDate: null, amount: '$75.000 CLP', method: 'N/A', status: 'Pendiente', statusClass: 'bg-warning text-dark' },
        { id: 'TRN-00126', clientName: 'Laura Martínez', contractId: 'CONTR-PREM-0727', scheduleDate: '2024-07-10', travelDateRange: '2024-10-01 a 2024-10-10', paymentDate: '2024-07-12', amount: '$50.000 CLP', method: 'Tarjeta de Débito', status: 'Rechazado', statusClass: 'bg-danger' },
        { id: 'TRN-00127', clientName: 'Pedro Rodríguez', contractId: 'CONTR-PREM-0728', scheduleDate: '2024-07-11', travelDateRange: '2024-11-11 a 2024-11-21', paymentDate: null, amount: '$200.000 CLP', method: 'N/A', status: 'Pendiente', statusClass: 'bg-warning text-dark' },
    ];

    // Options for the status filter dropdown
    const paymentStatusOptions = [
        { value: "", label: "Filtrar por estado" },
        { value: "Pagado", label: "Pagado" },
        { value: "Pendiente", label: "Pendiente" },
        { value: "Rechazado", label: "Rechazado" },
    ];

    useEffect(() => {
        // Simulate API call to fetch payments
        setIsLoading(true);
        setError(null);
        setTimeout(() => {
            setPayments(mockPayments);
            setIsLoading(false);
        }, 500);
    }, []);

    // Define columns for the DynamicTable
    const paymentColumns = [
        { header: 'ID Transacción', accessor: 'id' },
        { header: 'Nombre Cliente', accessor: 'clientName' },
        { header: 'Código Contrato', accessor: 'contractId' },
        { header: 'Fecha Agendamiento', accessor: 'scheduleDate' },
        { header: 'Fecha de Viaje (Rango)', accessor: 'travelDateRange' },
        { header: 'Fecha de Pago', accessor: 'paymentDate' },
        { header: 'Monto de Pago', accessor: 'amount' },
        { header: 'Método de Pago', accessor: 'method' },
        {
            header: 'Estado',
            render: (payment) => (
                <span className={`badge rounded-pill ${payment.statusClass}`}>
                    {payment.status}
                </span>
            )
        },
    ];

    // Define actions for the DynamicTable
    const paymentActions = [
        {
            icon: <EyeIcon />,
            tooltip: 'Ver Detalle del Pago',
            className: 'btn-outline-info-checkin',
            onClick: (payment) => console.log('View payment:', payment.id) // Replace with actual navigation/modal logic
        },
        {
            icon: <TrashFillIcon />,
            tooltip: 'Eliminar Registro de Pago',
            className: 'btn-outline-danger-checkin',
            onClick: (payment) => console.log('Delete payment:', payment.id) // Replace with actual deletion logic
        },
    ];

    // Define which columns are searchable
    const searchableColumns = ['id', 'clientName', 'contractId', 'scheduleDate', 'amount', 'method'];

    // Apply search and filter logic
    const filteredPayments = payments.filter(payment => {
        const matchesSearchTerm = searchTerm.trim() === '' ||
            searchableColumns.some(column => {
                const value = String(payment[column]).toLowerCase();
                return value.includes(searchTerm.toLowerCase());
            });

        const matchesFilterStatus = filterStatus === '' || payment.status === filterStatus;

        return matchesSearchTerm && matchesFilterStatus;
    });

    const totalItems = filteredPayments.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);

    // Calculate the start and end index for the current page
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedPayments = filteredPayments.slice(startIndex, endIndex);

    // Reset current page when search term or filter changes
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, filterStatus]);

    return (
        <>
            <FormSpinnerOverlay isLoading={isLoading} />
            <div className="pt-3 pb-2">
                <h1 className="h2 mb-3">Estados de Pago</h1>
                <p className="text-muted text-start pb-0">Listado y seguimiento de los estados de pago de los clientes.</p>
            </div>

            <div className="row g-3 mb-3 align-items-center d-flex justify-content-between">
                <div className="col-md-8">
                    <div className="input-group">
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Buscar por ID, cliente, contrato..."
                            aria-label="Buscar pagos"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <button className="btn btn-outline-secondary" type="button">
                            <SearchIcon />
                        </button>
                    </div>
                </div>
                <div className="col-md-4">
                    <select
                        className="form-select"
                        id="filtroEstadoPago"
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                    >
                        {paymentStatusOptions.map(option => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </select>
                </div>
            </div>

            <div className="row text-start mb-4">
                <div className="col-12">
                    <div className="card card-metric border-0">
                        <div className="card-body px-md-2 px-0">
                            {isLoading && <FormSpinnerOverlay isLoading={isLoading} />}
                            {error && <AlertMessage type="danger" message={`Error al cargar los pagos: ${error}`} />}
                            {!isLoading && !error && filteredPayments.length === 0 && (
                                <AlertMessage type="info" message="No hay estados de pago que coincidan con los criterios." />
                            )}

                            {!isLoading && !error && filteredPayments.length > 0 && (
                                <DynamicTable
                                    data={paginatedPayments}
                                    columns={paymentColumns}
                                    actions={paymentActions}
                                    keyAccessor="id"
                                />
                            )}

                            {totalPages > 1 && (
                                <Pagination
                                    currentPage={currentPage}
                                    itemsPerPage={itemsPerPage}
                                    totalItems={totalItems}
                                    onPageChange={(page) => setCurrentPage(page)}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <div className="copyright mt-auto text-center">
                <p>© 2025 Checkin. Todos los derechos reservados. Desarrollado por <a href="https://antudigital.cl" target="_blank" rel="noopener noreferrer">Antü Digital</a></p>
            </div>
        </>
    );
};

export default PaymentListAdminScreen;